// Show-condition rules for the player survey. SurveyService.VisibilityResolver on the server applies the same rules on submit.

export const SurveyQuestionType = {
  _SINGLE_CHOICE: 1,
  _MULTIPLE_CHOICE: 2,
  _SHORT_TEXT: 3,
  _LONG_TEXT: 4,
  _RATING: 5,
  _IMAGE_UPLOAD: 6,
};

export const SurveyRewardStatus = { _PENDING: 0, _CREDITED: 1, _FAILED: 2 };
export const SurveyFreeSpinStatus = { _NOT_CONFIGURED: 0, _PENDING: 1, _GIVEN: 2, _FAILED: 3 };

export const SURVEY_MAX_IMAGE_BYTES = 5 * 1024 * 1024;
export const SURVEY_IMAGE_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"];
const DEFAULT_SHORT_TEXT_LENGTH = 200;
const DEFAULT_LONG_TEXT_LENGTH = 2000;
const DEFAULT_MAX_IMAGES = 3;
const GENERAL_SECTION = "GENERAL";

export function isChoiceQuestion(question) {
  return question.questionType === SurveyQuestionType._SINGLE_CHOICE || question.questionType === SurveyQuestionType._MULTIPLE_CHOICE;
}

export function acceptsImages(question) {
  return question.questionType === SurveyQuestionType._IMAGE_UPLOAD || question.allowImage;
}

export function getTextMaxLength(question) {
  if (question.maxLength) {
    return question.maxLength;
  }
  return question.questionType === SurveyQuestionType._SHORT_TEXT ? DEFAULT_SHORT_TEXT_LENGTH : DEFAULT_LONG_TEXT_LENGTH;
}

export function getOtherMaxLength(question) {
  return question.maxLength || DEFAULT_SHORT_TEXT_LENGTH;
}

export function getMaxImages(question) {
  return question.questionType === SurveyQuestionType._IMAGE_UPLOAD && question.maxSelect ? question.maxSelect : DEFAULT_MAX_IMAGES;
}

/// Resolves which questions and options are visible for the current answers.
export function createResolver(questions, answers) {
  const questionById = {};
  questions.forEach((question) => (questionById[question.id] = question));
  const visibleCache = {};
  const selectedCache = {};
  const resolving = {};

  function visibleOptionsOf(question) {
    const parentSelected = question.parentQuestionId ? selectedOf(question.parentQuestionId) : [];
    let sourceCodes = null;
    if (question.optionSourceQuestionId) {
      const source = questionById[question.optionSourceQuestionId];
      const sourceSelected = selectedOf(question.optionSourceQuestionId);
      sourceCodes = (source ? source.options : [])
        .filter((option) => sourceSelected.includes(option.id))
        .map((option) => (option.optionCode || "").toUpperCase());
    }
    return (question.options || []).filter((option) => {
      const parents = option.parentOptionIds || [];
      if (parents.length > 0 && !parents.some((id) => parentSelected.includes(id))) {
        return false;
      }
      return sourceCodes === null || sourceCodes.includes((option.optionCode || "").toUpperCase());
    });
  }

  function isVisible(questionId) {
    if (visibleCache[questionId] !== undefined) {
      return visibleCache[questionId];
    }
    const question = questionById[questionId];
    if (!question || resolving[questionId]) {
      return false;
    }
    resolving[questionId] = true;
    let visible = true;
    if (question.parentQuestionId) {
      const parentSelected = selectedOf(question.parentQuestionId);
      const parentOptionIds = question.parentOptionIds || [];
      visible = parentOptionIds.length === 0 ? parentSelected.length > 0 : parentOptionIds.some((id) => parentSelected.includes(id));
    }
    if (visible && isChoiceQuestion(question)) {
      visible = visibleOptionsOf(question).length > 0;
    }
    delete resolving[questionId];
    visibleCache[questionId] = visible;
    return visible;
  }

  function selectedOf(questionId) {
    if (selectedCache[questionId] !== undefined) {
      return selectedCache[questionId];
    }
    let selected = [];
    const answer = answers[questionId];
    if (isVisible(questionId) && answer && answer.optionIds) {
      const allowed = visibleOptionsOf(questionById[questionId]).map((option) => option.id);
      selected = answer.optionIds.filter((id) => allowed.includes(id));
    }
    selectedCache[questionId] = selected;
    return selected;
  }

  return {
    isVisible,
    selectedOf,
    visibleOptions: (questionId) => (isVisible(questionId) ? visibleOptionsOf(questionById[questionId]) : []),
  };
}

/// Groups visible questions into stages by section code, in question order.
export function buildStages(questions, resolver) {
  const stages = [];
  questions.forEach((question) => {
    if (!resolver.isVisible(question.id)) {
      return;
    }
    const code = question.sectionCode || GENERAL_SECTION;
    let stage = stages.find((item) => item.code === code);
    if (!stage) {
      stage = { code, questions: [] };
      stages.push(stage);
    }
    stage.questions.push(question);
  });
  return stages;
}

/// Distinct SectionCode values in first-seen question order (for intro / thank-you steppers).
/// Null / empty SectionCode becomes GENERAL.
export function buildSectionSteps(questions) {
  const steps = [];
  const seen = new Set();
  (questions || []).forEach((question) => {
    const code = question.sectionCode || GENERAL_SECTION;
    const key = String(code).toUpperCase();
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    steps.push({ code, key });
  });
  return steps;
}

/// Section A..K titles reuse the label of the matching option in the improvement-area question.
export function getSectionTitle(code, questions, t, i18n) {
  const normalized = String(code || GENERAL_SECTION).toUpperCase();
  const key = "SURVEY_SECTION_" + normalized;
  if (i18n.exists(key)) {
    return t(key);
  }
  for (const question of questions) {
    if (String(question.sectionCode || "").toUpperCase() === normalized) {
      continue;
    }
    const option = (question.options || []).find((item) => String(item.optionCode || "").toUpperCase() === normalized);
    if (option) {
      return option.label;
    }
  }
  return code || GENERAL_SECTION;
}

const QUESTION_CODE_PATTERN = /^Q(\d+)(?:[.\-](\d+))?$/i;

/// Progress within a QN-N group. Q8-1 and Q8-2 are part 1 of 2 and part 2 of 2. A code with no sub-number is 1 of 1.
export function getQuestionGroupProgress(question, questions) {
  const code = String((question && question.questionCode) || "").trim();
  const match = code.match(QUESTION_CODE_PATTERN);
  if (!match || !match[2]) {
    return { code, current: 1, total: 1, percent: 100, showBar: false };
  }
  const base = match[1];
  const group = (questions || [])
    .map((item, index) => {
      const itemMatch = String(item.questionCode || "").trim().match(QUESTION_CODE_PATTERN);
      if (!itemMatch || itemMatch[1] !== base || !itemMatch[2]) {
        return null;
      }
      return { id: item.id, part: Number(itemMatch[2]), index };
    })
    .filter(Boolean)
    .sort((a, b) => a.part - b.part || a.index - b.index);
  const found = group.findIndex((item) => item.id === question.id);
  const current = found >= 0 ? found + 1 : 1;
  const total = Math.max(group.length, 1);
  return { code, current, total, percent: Math.round((current / total) * 100), showBar: true };
}

/// Returns { [questionId]: errorKey } for one question.
export function validateQuestion(question, answers, files, resolver) {
  if (!question) {
    return {};
  }
  return validateStage({ questions: [question] }, answers, files, resolver);
}

/// Returns { [questionId]: errorKey } for the questions of one stage.
export function validateStage(stage, answers, files, resolver) {
  const errors = {};
  (stage && stage.questions ? stage.questions : []).forEach((question) => {
    const answer = answers[question.id] || {};
    if (isChoiceQuestion(question)) {
      const selected = resolver.selectedOf(question.id);
      if (question.isRequired && selected.length === 0) {
        errors[question.id] = "SURVEY_REQUIRED_FIELD";
        return;
      }
      if (question.questionType === SurveyQuestionType._MULTIPLE_CHOICE && question.maxSelect && selected.length > question.maxSelect) {
        errors[question.id] = "SURVEY_MAX_SELECT_EXCEEDED";
        return;
      }
      const otherPicked = resolver.visibleOptions(question.id).some((option) => option.isOther && selected.includes(option.id));
      if (otherPicked && !(answer.otherText || "").trim()) {
        errors[question.id] = "SURVEY_PLEASE_SPECIFY_OTHER";
      }
    } else if (question.questionType === SurveyQuestionType._SHORT_TEXT || question.questionType === SurveyQuestionType._LONG_TEXT) {
      const text = (answer.text || "").trim();
      if (question.isRequired && text.length === 0) {
        errors[question.id] = "SURVEY_REQUIRED_FIELD";
      } else if (text.length > getTextMaxLength(question)) {
        errors[question.id] = "SURVEY_TEXT_TOO_LONG";
      }
    } else if (question.questionType === SurveyQuestionType._RATING) {
      if (question.isRequired && !answer.value) {
        errors[question.id] = "SURVEY_REQUIRED_FIELD";
      }
    } else if (question.questionType === SurveyQuestionType._IMAGE_UPLOAD) {
      if (question.isRequired && (files[question.id] || []).length === 0) {
        errors[question.id] = "SURVEY_REQUIRED_FIELD";
      }
    }
  });
  return errors;
}

/// Answers of visible questions only, with option ids limited to the visible options.
export function buildAnswerPayload(questions, answers, resolver) {
  return questions
    .filter((question) => resolver.isVisible(question.id))
    .map((question) => {
      const answer = answers[question.id] || {};
      const optionIds = isChoiceQuestion(question) ? resolver.selectedOf(question.id) : [];
      const otherPicked = resolver.visibleOptions(question.id).some((option) => option.isOther && optionIds.includes(option.id));
      return {
        questionId: question.id,
        optionIds,
        otherText: otherPicked ? (answer.otherText || "").trim() : null,
        text: (answer.text || "").trim() || null,
        value: answer.value || null,
      };
    });
}
